import React, {useEffect, useState} from "react";
import {DataTableColumn} from "../../../components/common/datatable/DataTableTypes";
import {
    createClickableLink,
    SearchSelectWithIndeterminateBoolean
} from "../../../components/common/datatable/DataTableUtils";
import {SearchInputWrapper} from "../../../components/common/datatable/filter/SearchInputWrapper";
import {SearchSelectWithList} from "../../../components/common/datatable/filter/SearchSelectWrapper";
import {formatAsCurrency} from "../../../helpers/utils/TextUtils";
import {formatValueAsDateAndTime} from "../../../helpers/utils/DateUtils";
import {SearchDatePickerWrapper} from "../../../components/common/datatable/filter/SearchDatePickerWrapper";
import Breadcrumb from "../../../components/common/Breadcrumbs";
import {DataTable} from "../../../components/common/datatable/DataTable";
import {useGetAllCustomersListQuery} from "../../customer/customerApi";
import {useDeletePaymentsMutation, useLazyExportPaymentsDataQuery, useSearchPaymentQuery} from "./paymentApi";
import {useLazyGetBranchByCustomerIdQuery} from "../../customer/branchContactApi";
import IconButton from "../../../components/common/form/IconButton";
import {FaUpload} from "react-icons/fa";
import UploadPaymentModal from "./modal/UploadPaymentModal";
import FailedPaymentsModal from "./modal/FailedPaymentsModal";
import {useFindUserSettingsByGroupNameQuery} from "../../../components/common/userSetting/userSettingsApi";

const Payment = () => {

    const [getBranchDataApi, getBranchResponse] = useLazyGetBranchByCustomerIdQuery();
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showFailedPaymentsModal, setShowFailedPaymentsModal] = useState(false);
    const [responseObj, setResponseObj] = useState({});
    const [deletePayments] = useDeletePaymentsMutation();
    const {data: userSetting} = useFindUserSettingsByGroupNameQuery("Payments");
    const [exportPaymentsAction] = useLazyExportPaymentsDataQuery();
    const [crumbs] = useState([
        {content: "Home", url: "/home"},
        {content: "Payment", url: ""}]
    )

    const customerData = useGetAllCustomersListQuery("");

    useEffect(() => {
        document.title = 'Payments';
    }, []);


    const columns: DataTableColumn[] = [
        {
            label: "Order No.", accessor: "customerOrderNumber", visibility: {hasShowSetting: false},
            cell: (props: any) => {
                if (!props.row.original.order?.id) {
                    return <>
                        {props.value}
                    </>
                }
                let path = "/orders/" + props.row.original.order.id;
                return <>{createClickableLink(path, props.value, userSetting)}</>;
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Order No..."}
                                                     name={"customerOrderNumber"}
                                                     accessor={"customerOrderNumber"}/>,
            }],
        },

        {
            label: "Customer",
            accessor: "customerName",
            visibility: {hasShowSetting: false},
            filterOptions: [{
                dependentColumns: ["Branch"],
                filterComponent: SearchSelectWithList(customerData?.data,
                    (customer: {
                        customerName: any;
                        customerNumber: any;
                    }) => `${customer.customerName} (${customer.customerNumber})`,
                    "id", "Select Customer...", false, "customerId"),
                overrideFilterAccessor: "customerId",
            }],
        },
        {
            label: "Branch", accessor: "order.branchId", visibility: {hasShowSetting: true}, canSort: false,
            cell: (props: any) => {
                return <>
                    <span>{props.row.original.order !== null ? props.row.original.order?.branchDTO?.name : 'N/A'}</span>
                </>
            },
            filterOptions: [{
                filterComponent: SearchSelectWithList(getBranchResponse?.data && getBranchResponse?.data.data, "name", "id", "Select Branch...", false, "branchId"),
                onDependencyUpdated: (value: any) => {
                    getBranchDataApi(value);
                },
                overrideFilterAccessor: "branchId",
            }]
        },

        {
            label: "Amount",
            accessor: "amount",
            visibility: {hasShowSetting: false},
            cell: (props: any) => (
                <span>{formatAsCurrency(props.value)}</span>
            ),
            filterOptions: [{
                filterComponent: (
                    <SearchInputWrapper
                        type={"number"}
                        placeholder={"Enter Amount..."}
                        name={"amount"}
                        accessor={"amount"}
                    />
                ),
            }],
        },


        {
            label: "Check No.", accessor: "checkNumber", visibility: {hasShowSetting: true},
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Check No..."}
                                                     name={"checkNumber"} accessor={"checkNumber"}/>,
            }],
        },

        {
            label: "Paid Date", accessor: "paidDate", visibility: {hasShowSetting: false},
            cell: (props: any) => {
                return formatValueAsDateAndTime(props.value, "MM/DD/YYYY");
            },
            filterOptions: [
                {
                    filterComponent: <SearchDatePickerWrapper accessor={"paidDateFrom"}/>,
                    overrideFilterAccessor: "paidDateFrom",
                    overrideFilterLabel: "Paid Date - From",
                },
                {
                    filterComponent: <SearchDatePickerWrapper accessor={"paidDateTo"}/>,
                    overrideFilterAccessor: "paidDateTo",
                    overrideFilterLabel: "Paid Date - To",
                }]
        },

        {
            label: "File Name", accessor: "fileName", visibility: {hasShowSetting: true},
            cell: (props: any) => {
                return <><span
                    title={props.row.original.fileName}>{props.row.original.fileName.length > 20 ? props.row.original.fileName.substring(0, 20) + '...' : props.row.original.fileName}</span></>
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter File Name..."}
                                                     name={"fileName"} accessor={"fileName"}/>,
            }],
        },

        {
            label: "Upload Date", accessor: "creationDate", visibility: {hasShowSetting: false},
            cell: (props: any) => {
                return formatValueAsDateAndTime(props.value);
            },
            filterOptions: [
                {
                    filterComponent: <SearchDatePickerWrapper accessor={"createdDateFrom"}
                    />,
                    overrideFilterAccessor: "createdDateFrom",
                    overrideFilterLabel: "Upload Date - From",
                },
                {
                    filterComponent: <SearchDatePickerWrapper accessor={"createdDateTo"}
                    />,
                    overrideFilterAccessor: "createdDateTo",
                    overrideFilterLabel: "Upload Date - To",
                }]
        },

        {
            label: "Unmatched", accessor: "unmatched", visibility: {hasShowSetting: false, canColumnShow: false},
            filterOptions: [
                {
                    filterComponent: SearchSelectWithIndeterminateBoolean("Select Match Status...", "unmatched", "Exclude Matched", "Exclude Unmatched"),
                    overrideFilterAccessor: "unmatched",
                    overrideFilterLabel: "Match Status",
                }]
        },
        {
            label: "Overpaid", accessor: "overpaid", visibility: {hasShowSetting: false, canColumnShow: false},
            filterOptions: [
                {
                    filterComponent: SearchSelectWithIndeterminateBoolean("Select Over Paid...", "overpaid", "Exclude Matched", "Exclude Unmatched"),
                    overrideFilterAccessor: "overpaid",
                    overrideFilterLabel: "Over Paid",
                }]
        },
        {
            label: "Underpaid", accessor: "underpaid", visibility: {hasShowSetting: false, canColumnShow: false},
            filterOptions: [
                {
                    filterComponent: SearchSelectWithIndeterminateBoolean("Select Under Paid...", "underpaid", "Exclude Matched", "Exclude Unmatched"),
                    overrideFilterAccessor: "underpaid",
                    overrideFilterLabel: "Under Paid",
                }]
        },

    ]
    return (
        <div className="px-4 py-2">
            <div className="flex justify-between mb-2.5">
                <Breadcrumb crumbs={crumbs}/>
            </div>
            <DataTable
                columns={columns}
                actions={{
                    fetchAction: useSearchPaymentQuery,
                    exportAction: exportPaymentsAction,
                    deleteAction: deletePayments
                }}
                defaultSortOptions={{
                    accessor: "creationDate",
                    direction: "desc"
                }}
                hasBulkDelete={true}
                rowSelectionOptions={{
                    enableMultiRowSelection: true,
                    onSelectionChange: () => {
                    }

                }}
                buttonOptions={{
                    addButton:
                        {
                            button: <IconButton
                                icon={<FaUpload/>}
                                hasBackground={true}
                                onClick={() => {
                                    setShowUploadModal(true);
                                }}
                                title={"Upload Payments"}
                            />,
                            permissions: ["PAYMT-C"]
                        },
                }}

                userSettingGroup={"Payment"}
                basePermission={"PAYMT"}
            />


            {showUploadModal && customerData &&
                <UploadPaymentModal show={showUploadModal} setShow={setShowUploadModal} customerData={customerData}
                                    setShowFailedPaymentsModal={setShowFailedPaymentsModal}
                                    setResponseObj={setResponseObj}/>}

            {showFailedPaymentsModal &&
                <FailedPaymentsModal show={showFailedPaymentsModal} setShow={setShowFailedPaymentsModal}
                                     responseObj={responseObj}/>}
        </div>
    );
};

export default Payment;

